<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { flowsAPI } from 'dashboard/api/flows';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  conversationId: { type: Number, required: true },
});

const emit = defineEmits(['close', 'started']);

const { t } = useI18n();

const flows = ref([]);
const isLoading = ref(false);
const isStarting = ref(false);
const selectedLanguage = ref('');
const selectedCategory = ref('');
const selectedFlow = ref(null);
const searchQuery = ref('');

const LANGUAGE_LABELS = {
  en: 'English',
  ar: 'Arabic',
  es: 'Spanish',
  fr: 'French',
  ur: 'Urdu',
  pt: 'Portuguese',
  sw: 'Swahili',
  hi: 'Hindi',
  zh: 'Mandarin',
  ru: 'Russian',
};

const CATEGORY_LABELS = {
  evangelism: 'Evangelism',
  discipleship: 'Discipleship',
  seeker: 'Seeker',
  'follow-up': 'Follow-up',
  prayer: 'Prayer',
};

const filteredFlows = computed(() => {
  return flows.value.filter(f => {
    const matchLang =
      !selectedLanguage.value || f.language === selectedLanguage.value;
    const matchCat =
      !selectedCategory.value || f.category === selectedCategory.value;
    const matchQ =
      !searchQuery.value ||
      f.name.toLowerCase().includes(searchQuery.value.toLowerCase());
    return matchLang && matchCat && matchQ;
  });
});

const groupedFlows = computed(() => {
  return filteredFlows.value.reduce((groups, flow) => {
    const cat = CATEGORY_LABELS[flow.category] || flow.category;
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(flow);
    return groups;
  }, {});
});

const availableLanguages = computed(() => {
  const langs = [...new Set(flows.value.map(f => f.language))];
  return langs.map(l => ({ code: l, label: LANGUAGE_LABELS[l] || l }));
});

async function loadFlows() {
  isLoading.value = true;
  try {
    const { data } = await flowsAPI.list({ active: 'true' });
    flows.value = data;
  } catch {
    useAlert('Could not load flows. Please try again.');
  } finally {
    isLoading.value = false;
  }
}

async function startFlow() {
  if (!selectedFlow.value) return;
  isStarting.value = true;
  try {
    const { data } = await flowsAPI.trigger(
      selectedFlow.value.id,
      props.conversationId
    );
    emit('started', data);
    emit('close');
    useAlert(`Flow "${selectedFlow.value.name}" started!`);
  } catch (e) {
    const msg = e?.response?.data?.error || 'Could not start flow. Try again.';
    useAlert(msg);
  } finally {
    isStarting.value = false;
  }
}

onMounted(loadFlows);
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    @click.self="emit('close')"
  >
    <div
      class="w-full max-w-lg bg-n-solid-1 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh]"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-5 py-4 bg-woot-500 text-white shrink-0"
      >
        <div class="flex items-center gap-2">
          <span class="i-lucide-zap size-5" />
          <span class="text-base font-semibold">{{
            t('MINISTRY.FLOW_LAUNCHER.TITLE')
          }}</span>
        </div>
        <button class="text-white/70 hover:text-white" @click="emit('close')">
          <span class="i-lucide-x size-5" />
        </button>
      </div>

      <!-- Filters -->
      <div class="px-4 py-3 border-b border-n-weak shrink-0 flex gap-2">
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="t('MINISTRY.FLOW_LAUNCHER.SEARCH_PLACEHOLDER')"
          class="flex-1 text-sm px-3 py-1.5 rounded-md border border-n-weak bg-n-solid-1 focus:outline-none focus:border-woot-500"
        />
        <select
          v-model="selectedLanguage"
          class="text-sm px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-1 focus:outline-none focus:border-woot-500"
        >
          <option value="">
            {{ t('MINISTRY.FLOW_LAUNCHER.ALL_LANGUAGES') }}
          </option>
          <option
            v-for="lang in availableLanguages"
            :key="lang.code"
            :value="lang.code"
          >
            {{ lang.label }}
          </option>
        </select>
        <select
          v-model="selectedCategory"
          class="text-sm px-2 py-1.5 rounded-md border border-n-weak bg-n-solid-1 focus:outline-none focus:border-woot-500"
        >
          <option value="">
            {{ t('MINISTRY.FLOW_LAUNCHER.ALL_CATEGORIES') }}
          </option>
          <option
            v-for="(label, key) in CATEGORY_LABELS"
            :key="key"
            :value="key"
          >
            {{ label }}
          </option>
        </select>
      </div>

      <!-- Flow List -->
      <div class="flex-1 overflow-y-auto px-4 py-3">
        <div v-if="isLoading" class="flex justify-center py-8">
          <span class="i-lucide-loader-2 size-6 animate-spin text-woot-500" />
        </div>
        <div
          v-else-if="Object.keys(groupedFlows).length === 0"
          class="text-center py-8 text-n-slate-8 text-sm"
        >
          {{ t('MINISTRY.FLOW_LAUNCHER.NO_FLOWS') }}
        </div>
        <div v-else>
          <div
            v-for="(group, category) in groupedFlows"
            :key="category"
            class="mb-4"
          >
            <h4
              class="text-xs font-semibold text-n-slate-8 uppercase tracking-wider mb-2"
            >
              {{ category }}
            </h4>
            <div
              v-for="flow in group"
              :key="flow.id"
              class="flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-all mb-1.5"
              :class="
                selectedFlow?.id === flow.id
                  ? 'border-woot-500 bg-woot-500/5'
                  : 'border-n-weak hover:border-woot-300 hover:bg-n-slate-1'
              "
              @click="selectedFlow = flow"
            >
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-n-slate-12 truncate">
                  {{ flow.name }}
                </p>
                <p
                  v-if="flow.description"
                  class="text-xs text-n-slate-8 truncate"
                >
                  {{ flow.description }}
                </p>
              </div>
              <div class="flex items-center gap-1.5 shrink-0">
                <span
                  class="text-[10px] px-1.5 py-0.5 rounded-full bg-n-slate-3 text-n-slate-9"
                >
                  {{ LANGUAGE_LABELS[flow.language] || flow.language }}
                </span>
                <span
                  class="text-[10px] px-1.5 py-0.5 rounded-full bg-n-slate-3 text-n-slate-9"
                >
                  {{
                    t('MINISTRY.FLOW_LAUNCHER.STEPS_COUNT', {
                      count: flow.steps_count,
                    })
                  }}
                </span>
              </div>
              <span
                v-if="selectedFlow?.id === flow.id"
                class="i-lucide-check-circle size-4 text-woot-500 shrink-0"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Preview -->
      <div
        v-if="selectedFlow"
        class="px-4 py-3 border-t border-n-weak bg-n-slate-1 shrink-0"
      >
        <p class="text-xs text-n-slate-8 mb-1 font-medium">
          {{ t('MINISTRY.FLOW_LAUNCHER.PREVIEW_LABEL') }}
        </p>
        <p class="text-xs text-n-slate-11 line-clamp-3 italic">
          {{
            selectedFlow.steps?.[0]?.message_text ||
            t('MINISTRY.FLOW_LAUNCHER.PREVIEW_EMPTY')
          }}
        </p>
      </div>

      <!-- Footer -->
      <div
        class="px-4 py-3 border-t border-n-weak flex justify-end gap-2 shrink-0"
      >
        <button
          class="px-4 py-1.5 text-sm rounded-md border border-n-weak text-n-slate-9 hover:text-n-slate-12 transition-colors"
          @click="emit('close')"
        >
          {{ t('MINISTRY.FLOW_LAUNCHER.CANCEL') }}
        </button>
        <button
          class="px-4 py-1.5 text-sm rounded-md bg-woot-500 text-white font-medium hover:bg-woot-600 transition-colors disabled:opacity-50"
          :disabled="!selectedFlow || isStarting"
          @click="startFlow"
        >
          <span
            v-if="isStarting"
            class="i-lucide-loader-2 size-4 animate-spin mr-1"
          />
          {{
            isStarting
              ? t('MINISTRY.FLOW_LAUNCHER.STARTING')
              : t('MINISTRY.FLOW_LAUNCHER.START_FLOW')
          }}
        </button>
      </div>
    </div>
  </div>
</template>
